<template>
  <div class="header" @mouseleave="onHeaderLeave">
    <div class="header-content">
      <div style="display: flex">
        <NuxtLink :to="localePath('/')" class="header-logo">
          <img src="/static/images/nicsrsLogo.png" alt="NicSRS" />
        </NuxtLink>
        <div class="header-links" :class="{ 'is-phone-version': phoneNavOpen }" :style="phoneNavStyle">
          <div
            v-for="item in navItems"
            :key="item.name"
            :data-name="item.name"
            class="nav-item"
            :class="{ active: activeSubmenu === item.name }"
            @mouseenter="showSubmenu(item.name)"
          >
            {{ item.label }}
            <span v-if="item.hasSub" class="nav-arrow"></span>
          </div>
        </div>
      </div>

      <div class="header-right">
        <div class="">
          <span class="currency-label">{{ t('header.currency') }}</span>
          <!-- <span class="nav-arrow"></span> -->
        </div>
        <div class="line"></div>
        <div class="header-lang" @click.stop="langDropdownOpen = !langDropdownOpen">
          <!-- <span class="lang-icon"></span> -->
          <span>{{ currentLocaleLabel }}</span>
          <!-- <span class="lang-arrow" :class="{ open: langDropdownOpen }">▼</span> -->
          <ul v-show="langDropdownOpen" class="lang-dropdown" @click.stop>
            <li
              v-for="loc in availableLocales"
              :key="loc.code"
              :class="{ active: locale === loc.code }"
              @click="selectLocale(loc.code)"
            >
              <a :href="fullLocaleUrl(loc.code)" @click.prevent>{{ loc.name }}</a>
            </li>
          </ul>
        </div>

        <a class="header-signin" href="https://console.nicsrs.com/login">{{ t('header.signIn') }}</a>
        <a class="header-cta" href="https://console.nicsrs.com/register" target="_blank">{{
          t('header.createAccount')
        }}</a>
      </div>

      <div class="header-phone-btn" @click="togglePhoneNav"></div>
    </div>

    <!-- SSL submenu -->
    <div class="submenu" data-name="ssl" :style="submenuStyle('ssl')" @mouseleave="hideSubmenu">
      <div class="inner-bound submenu-content">
        <div class="product-left">
          <div class="content">
            <h3>{{ t('header.submenu.ssl.title') }}</h3>
            <p>
              {{ t('header.submenu.ssl.desc') }}
            </p>
            <NuxtLink :to="localePath('/ssl')">{{ t('header.submenu.ssl.cta') }} &rarr;</NuxtLink>
          </div>
        </div>
        <div class="product-right">
          <div class="submenu-grid submenu-grid-4">
            <div class="submenu-col">
              <p class="header-box-title">{{ t('header.submenu.ssl.byBrands') }}</p>
              <div class="submenu-list two-col">
                <NuxtLink :to="localePath('/brand/positivessl')">PositiveSSL</NuxtLink>
                <NuxtLink :to="localePath('/brand/globalsign-ssl')">GlobalSign</NuxtLink>
                <NuxtLink :to="localePath('/brand/sectigo-ssl')">Sectigo</NuxtLink>
                <NuxtLink :to="localePath('/brand/certum-ssl')">Certum</NuxtLink>
                <NuxtLink :to="localePath('/brand/digicert-ssl')">Digicert</NuxtLink>
                <NuxtLink :to="localePath('/brand/ssltrus-ssl')">sslTrus</NuxtLink>
                <NuxtLink :to="localePath('/brand/geotrust-ssl')">Geotrust</NuxtLink>
                <NuxtLink :to="localePath('/brand/actalis-ssl')">Actalis</NuxtLink>
                <NuxtLink :to="localePath('/brand/thawte-ssl')">Thawte</NuxtLink>
              </div>
            </div>
            <div class="submenu-col">
              <p class="header-box-title">{{ t('header.submenu.ssl.byType') }}</p>
              <div class="submenu-list">
                <NuxtLink :to="localePath('/ssl/dv-ssl')">DV SSL</NuxtLink>
                <NuxtLink :to="localePath('/ssl/ov-ssl')">OV SSL</NuxtLink>
                <NuxtLink :to="localePath('/ssl/ev-ssl')">EV SSL</NuxtLink>
                <NuxtLink :to="localePath('/ssl/wildcard-ssl')">Wildcard SSL</NuxtLink>
                <NuxtLink :to="localePath('/ssl/multi-domain-ssl')">Multi-Domain SSL</NuxtLink>
                <NuxtLink :to="localePath('/ssl/ip-ssl')">IP SSL</NuxtLink>
              </div>
            </div>
            <div class="submenu-col submenu-col-wide">
              <NuxtLink :to="localePath('/caas')" class="submenu-feature">
                <span class="feature-title">CaaS &rarr;</span>
                <span class="feature-desc">{{ t('header.submenu.ssl.featureCaaS') }}</span>
              </NuxtLink>
              <NuxtLink :to="localePath('/clm')" class="submenu-feature">
                <span class="feature-title">CLM &rarr;</span>
                <span class="feature-desc">{{ t('header.submenu.ssl.featureCLM') }}</span>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Digital submenu -->
    <div class="submenu" data-name="digital" :style="submenuStyle('digital')" @mouseleave="hideSubmenu">
      <div class="inner-bound submenu-content">
        <div class="product-left">
          <div class="content">
            <h3>{{ t('header.submenu.digital.title') }}</h3>
            <p>
              {{ t('header.submenu.digital.desc') }}
            </p>
          </div>
        </div>
        <div class="product-right">
          <div class="submenu-grid submenu-grid-3">
            <div class="submenu-col">
              <p class="header-box-title">{{ t('header.submenu.digital.signingCertificates') }}</p>
              <div class="submenu-list">
                <NuxtLink :to="localePath('/code-signing-certificate')">Code Signing Certificate</NuxtLink>
                <NuxtLink :to="localePath('/email-signing-certificate')">S/MIME Email Certificate</NuxtLink>
                <NuxtLink :to="localePath('/document-signing-certificate')">Document Signing Certificate</NuxtLink>
              </div>
            </div>
            <div class="submenu-col">
              <p class="header-box-title">{{ t('header.submenu.digital.markCertificates') }}</p>
              <div class="submenu-list">
                <NuxtLink :to="localePath('/verified-mark-certificate')">Verified Mark Certificates (VMC)</NuxtLink>
                <!-- <NuxtLink :to="localePath('/verified-mark-certificate')"
                  >Go to Verified Mark Certificates (VMC)</NuxtLink
                > -->
                <!-- <NuxtLink :to="localePath('/verified-mark-certificate')"
                  >Common Mark Certificates (CMC)</NuxtLink
                > -->
              </div>
            </div>
            <div class="submenu-col">
              <p class="header-box-title">{{ t('header.submenu.digital.signingSolution') }}</p>
              <div class="submenu-list">
                <NuxtLink :to="localePath('/cloud-code-signing')">Cloud Code Signing</NuxtLink>
                <NuxtLink :to="localePath('/cloud-mpki')">PKI</NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Domain submenu -->
    <div class="submenu" data-name="domain" :style="submenuStyle('domain')" @mouseleave="hideSubmenu">
      <div class="inner-bound submenu-content">
        <div class="product-left">
          <div class="content">
            <h3>{{ t('header.submenu.domain.title') }}</h3>
            <p>
              {{ t('header.submenu.domain.desc') }}
            </p>
          </div>
        </div>
        <div class="product-right">
          <div class="submenu-grid submenu-grid-3">
            <div class="submenu-col">
              <p class="header-box-title">{{ t('header.submenu.domain.findDomain') }}</p>
              <div class="submenu-list">
                <NuxtLink :to="localePath('/domain')">Domain Registration</NuxtLink>
                <NuxtLink :to="localePath('/domain-transfer')">Domain Transfer</NuxtLink>
                <NuxtLink :to="localePath('/ccTLDs')">ccTLD Registration</NuxtLink>
                <NuxtLink :to="localePath('/domain')">gTLD Domain Extensions</NuxtLink>
              </div>
            </div>
            <div class="submenu-col">
              <p class="header-box-title">{{ t('header.submenu.domain.domainService') }}</p>
              <div class="submenu-list">
                <NuxtLink :to="localePath('/dns')">DNS</NuxtLink>
                <NuxtLink :to="localePath('/domain')">Domain Broker Service</NuxtLink>
              </div>
            </div>
            <div class="submenu-col">
              <p class="header-box-title">{{ t('header.submenu.domain.domainTools') }}</p>
              <div class="submenu-list">
                <NuxtLink :to="localePath('/whois-lookup')">Whois Lookup</NuxtLink>
                <NuxtLink :to="localePath('/dns-lookup')">DNS Lookup</NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Hosting submenu -->
    <div class="submenu" data-name="hosting" :style="submenuStyle('hosting')" @mouseleave="hideSubmenu">
      <div class="inner-bound submenu-content">
        <div class="product-left">
          <div class="content">
            <h3>{{ t('header.submenu.hosting.title') }}</h3>
            <p>
              {{ t('header.submenu.hosting.desc') }}
            </p>
          </div>
        </div>
        <div class="product-right">
          <div class="submenu-grid submenu-grid-2">
            <div class="submenu-col">
              <div class="submenu-list">
                <NuxtLink :to="localePath('/hosting/type/linux')" class="submenu-link-arrow"
                  >Linux Web Hosting</NuxtLink
                >
                <NuxtLink :to="localePath('/hosting/type/windows')" class="submenu-link-arrow"
                  >Windows Web Hosting</NuxtLink
                >
                <NuxtLink :to="localePath('/hosting/location/hong-kong')" class="submenu-link-arrow"
                  >Hong Kong Web Hosting</NuxtLink
                >
                <NuxtLink :to="localePath('/hosting/location/us')" class="submenu-link-arrow">US Web Hosting</NuxtLink>
              </div>
            </div>
            <div class="submenu-col">
              <p class="header-box-title">{{ t('header.submenu.hosting.serverManagement') }}</p>
              <div class="submenu-list">
                <NuxtLink :to="localePath('/plesk')">Plesk</NuxtLink>
                <NuxtLink :to="localePath('/cpanel')">cPanel</NuxtLink>
                <NuxtLink :to="localePath('/aapanel')">aaPanel</NuxtLink>
                <NuxtLink :to="localePath('/ispmanager')">ISPManeger</NuxtLink>
                <NuxtLink :to="localePath('/sitelock')">SiteLock</NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Support submenu -->
    <div class="submenu submenu-compact" data-name="support" :style="submenuStyle('support')" @mouseleave="hideSubmenu">
      <div class="inner-bound submenu-content">
        <div class="product-left">
          <div class="content">
            <h3>{{ t('header.submenu.support.title') }}</h3>
            <p>
              {{ t('header.submenu.support.desc') }}
            </p>
          </div>
        </div>
        <div class="product-right product-right-full">
          <div class="submenu-grid submenu-grid-4">
            <div class="submenu-col">
              <p class="header-box-title">{{ t('header.submenu.support.documentsTools') }}</p>
              <div class="submenu-list">
                <NuxtLink :to="localePath('/help-center')">{{ t('header.submenu.support.onlineHelp') }}</NuxtLink>
                <NuxtLink :to="localePath('/help-list/API')">{{ t('header.submenu.support.apiServices') }}</NuxtLink>
                <NuxtLink :to="localePath('/tools')">{{ t('tools.name') }}</NuxtLink>
              </div>
            </div>
            <div class="submenu-col">
              <p class="header-box-title">{{ t('header.submenu.support.supportServices') }}</p>
              <div class="submenu-list">
                <a href="#" rel="nofollow">{{ t('header.submenu.support.preSaleConsult') }}</a>
                <a href="#" rel="nofollow">{{ t('header.submenu.support.onlineSupport') }}</a>
                <a href="https://console.nicsrs.com/tickets/list" target="_blank" rel="nofollow">{{
                  t('header.submenu.support.ticketSystem')
                }}</a>
                <NuxtLink :to="localePath('/blog')">{{ t('header.submenu.support.newsBlog') }}</NuxtLink>
                <a href="https://console.nicsrs.com" target="_blank" rel="nofollow">{{
                  t('header.submenu.support.clientPortal')
                }}</a>
                <NuxtLink :to="localePath('/contact')">{{ t('header.submenu.support.contactUs') }}</NuxtLink>
              </div>
            </div>
            <div class="submenu-col">
              <p class="header-box-title">{{ t('header.submenu.support.addOnServices') }}</p>
              <div class="submenu-list">
                <a href="https://console.nicsrs.com/technical/service/buy/list" target="_blank" rel="nofollow">{{
                  t('header.submenu.support.sslInstallation')
                }}</a>
                <NuxtLink :to="localePath('/whql')">{{ t('header.submenu.support.whql') }}</NuxtLink>
                <a href="https://console.nicsrs.com/technical/service/buy/list" target="_blank" rel="nofollow">{{
                  t('header.submenu.support.expertTech')
                }}</a>
              </div>
            </div>
            <div class="submenu-col">
              <p class="header-box-title">{{ t('header.submenu.support.solutions') }}</p>
              <div class="submenu-list">
                <!-- <NuxtLink :to="localePath('/solution/secured-email')"
                  >{{ t("header.submenu.support.emailEncryption") }}</NuxtLink
                > -->
                <NuxtLink :to="localePath('/solution/pki')">{{ t('header.submenu.support.pkiSolution') }}</NuxtLink>
                <NuxtLink :to="localePath('/solution/ucm')">{{ t('header.submenu.support.ucm') }}</NuxtLink>
                <!-- <NuxtLink :to="localePath('/solution/foreign-trade-ecommerce')"
                  >{{ t("header.submenu.support.intlEcommerce") }}</NuxtLink
                > -->
                <!-- <NuxtLink :to="localePath('/solution/hosting')"
                  >{{ t("header.submenu.support.hostingSolution") }}</NuxtLink
                > -->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- About submenu -->
    <div class="submenu submenu-compact" data-name="about" :style="submenuStyle('about')" @mouseleave="hideSubmenu">
      <div class="inner-bound submenu-content">
        <div class="product-right product-right-full">
          <div class="submenu-grid submenu-grid-3">
            <div class="submenu-col">
              <p class="header-box-title">{{ t('header.submenu.about.aboutUs') }}</p>
              <div class="submenu-list">
                <NuxtLink :to="localePath('/about-us')">{{ t('header.submenu.about.companyInfo') }}</NuxtLink>
                <NuxtLink :to="localePath('/why-nicsrs')">{{ t('header.submenu.about.whyNicsrs') }}</NuxtLink>
                <NuxtLink :to="localePath('/ecosystem')">{{ t('ecosystem.title') }}</NuxtLink>
                <NuxtLink :to="localePath('/reseller')">{{ t('header.submenu.about.partner') }}</NuxtLink>
                <NuxtLink :to="localePath('/blog')">{{ t('header.submenu.about.blog') }}</NuxtLink>
              </div>
            </div>
            <div class="submenu-col">
              <p class="header-box-title">{{ t('header.submenu.about.contactUs') }}</p>
              <div class="submenu-list">
                <NuxtLink :to="localePath('/contact')">{{ t('header.submenu.about.contactUs') }}</NuxtLink>
                <NuxtLink :to="localePath('/reseller')">{{ t('header.submenu.about.resellerProgram') }}</NuxtLink>
                <NuxtLink :to="localePath('/affiliate-program')">{{
                  t('header.submenu.about.affiliateProgram')
                }}</NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const { locale, locales, setLocale, t } = useI18n()
const localePath = useLocalePath()
const switchLocalePath = useSwitchLocalePath()

function fullLocaleUrl(code) {
  const path = switchLocalePath(code)
  return `https://www.nicsrs.com${path}`
}

const activeSubmenu = ref('')
const phoneNavOpen = ref(false)
const langDropdownOpen = ref(false)

const navItems = computed(() => [
  { name: 'ssl', label: t('header.nav.ssl'), hasSub: true },
  { name: 'digital', label: t('header.nav.digital'), hasSub: true },
  { name: 'domain', label: t('header.nav.domain'), hasSub: true },
  { name: 'hosting', label: t('header.nav.hosting'), hasSub: true },
  { name: 'support', label: t('header.nav.support'), hasSub: true },
  { name: 'about', label: t('header.nav.about'), hasSub: true }
])

const availableLocales = computed(() =>
  (locales.value || locales || []).map((loc) => ({
    code: typeof loc === 'string' ? loc : loc.code,
    name: typeof loc === 'string' ? loc : loc.name || loc.code
  }))
)

const currentLocaleLabel = computed(() => {
  const loc = availableLocales.value.find((l) => l.code === locale.value)
  return loc?.name || locale.value
})

function selectLocale(code) {
  setLocale(code)
  langDropdownOpen.value = false
}

function closeLangDropdown(e) {
  if (!e.target.closest('.header-lang')) {
    langDropdownOpen.value = false
  }
}

onMounted(() => {
  if (process.client) {
    window.addEventListener('click', closeLangDropdown)
  }
})
onUnmounted(() => {
  if (process.client) {
    window.removeEventListener('click', closeLangDropdown)
  }
})

const phoneNavStyle = computed(() => {
  if (!phoneNavOpen.value) return {}
  return { display: 'flex' }
})

function submenuStyle(name) {
  return activeSubmenu.value === name ? { display: 'flex' } : { display: 'none' }
}

function showSubmenu(name) {
  activeSubmenu.value = name
}

function hideSubmenu() {
  activeSubmenu.value = ''
  if (phoneNavOpen.value) {
    phoneNavOpen.value = false
  }
}

function onHeaderLeave() {
  activeSubmenu.value = ''
  if (phoneNavOpen.value) {
    phoneNavOpen.value = false
  }
}

function togglePhoneNav() {
  phoneNavOpen.value = !phoneNavOpen.value
  if (phoneNavOpen.value) {
    activeSubmenu.value = 'ssl'
  } else {
    activeSubmenu.value = ''
  }
}
</script>

<style scoped>
.header-lang {
  position: relative;
}
.lang-arrow {
  font-size: 0.1rem;
  transition: transform 0.2s;
  margin-left: 0.02rem;
}
.lang-arrow.open {
  transform: rotate(180deg);
}
.lang-dropdown {
  position: absolute;
  top: calc(100% + 0.08rem);
  right: 0;
  min-width: 1.2rem;
  background: #fff;
  border-radius: 0.08rem;
  box-shadow: 0 0.04rem 0.12rem rgba(0, 0, 0, 0.12);
  list-style: none;
  padding: 0.06rem 0;
  margin: 0;
  z-index: 1000;
}
.lang-dropdown li {
  padding: 0.2rem;
  color: #0e1726;
  font-size: 0.14rem;
  cursor: pointer;
  white-space: nowrap;
  list-style: none;
  text-align: center;
  &:before {
    content: '';
    color: unset;
    font-size: unset;
  }
}
.lang-dropdown li a {
  color: inherit;
  text-decoration: none;
  display: block;
}
.lang-dropdown li:hover,
.lang-dropdown li.active {
  background: #f5f7fa;
  color: var(--theme-color);
}
</style>
